import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ThumbsUp, MessageSquare, Plus, User, Clock, Filter, Search } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CommunityFeedback = () => {
  const [filter, setFilter] = useState('all'); // all, popular, recent
  const [searchQuery, setSearchQuery] = useState('');
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  const feedbackItems = [
    {
      id: 1,
      title: 'Dark Mode Theme Support',
      description: 'Add a dark theme option for better coding experience during night hours.',
      author: 'DevMaster92',
      timestamp: '2025-01-08T10:30:00Z',
      votes: 425,
      comments: 63,
      status: 'under-review',
      category: 'UI/UX'
    },
    {
      id: 2,
      title: 'Keyboard Shortcuts for Code Editor',
      description: 'Implement VS Code-style keyboard shortcuts for faster coding and navigation.',
      author: 'CodeNinja',
      timestamp: '2025-01-07T15:45:00Z',
      votes: 312,
      comments: 41,
      status: 'planned',
      category: 'Developer Tools'
    },
    {
      id: 3,
      title: 'Selenium Grid Integration',
      description: 'Allow tests to run on Selenium Grid for parallel execution across multiple browsers.',
      author: 'TestingPro',
      timestamp: '2025-01-06T09:20:00Z',
      votes: 298,
      comments: 35,
      status: 'in-discussion',
      category: 'Testing Infrastructure'
    },
    {
      id: 4,
      title: 'Mobile-First Responsive Design',
      description: 'Optimize the entire platform for mobile devices with touch-friendly interfaces.',
      author: 'MobileExpert',
      timestamp: '2025-01-05T14:10:00Z',
      votes: 267,
      comments: 28,
      status: 'under-review',
      category: 'Mobile'
    },
    {
      id: 5,
      title: 'Video Tutorials Integration',
      description: 'Embed video explanations directly in missions for visual learners.',
      author: 'EduTech',
      timestamp: '2025-01-04T11:55:00Z',
      votes: 234,
      comments: 22,
      status: 'new',
      category: 'Education'
    },
    {
      id: 6,
      title: 'Code Snippet Sharing',
      description: 'Allow users to share and bookmark useful Selenium code snippets.',
      author: 'ShareCoder',
      timestamp: '2025-01-03T16:30:00Z',
      votes: 189,
      comments: 18,
      status: 'new',
      category: 'Community'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'planned': return 'bg-green-500/10 text-green-500';
      case 'under-review': return 'bg-blue-500/10 text-blue-500';
      case 'in-discussion': return 'bg-yellow-500/10 text-yellow-500';
      case 'new': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-muted/10 text-muted-foreground';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'planned': return 'Planned';
      case 'under-review': return 'Under Review';
      case 'in-discussion': return 'In Discussion';
      case 'new': return 'New';
      default: return 'Unknown';
    }
  };

  const filteredItems = feedbackItems?.filter(item => {
      if (filter === 'popular') return item?.votes > 250;
      if (filter === 'recent') return new Date(item.timestamp) > new Date('2025-01-06');
      return true;
    })?.filter(item => 
      item?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    )?.sort((a, b) => {
      if (filter === 'popular') return b?.votes - a?.votes;
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

  const handleVote = (itemId) => {
    // Simulate voting functionality
    console.log(`Voted for item ${itemId}`);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-3xl font-bold">Community Feedback</h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Your voice matters! Vote on proposed features, submit new ideas, and help prioritize development.
        </p>
      </div>
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              type="text"
              placeholder="Search suggestions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="pl-10 w-64"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select 
              value={filter} 
              onChange={(e) => setFilter(e?.target?.value)}
              className="bg-background border border-border rounded-md px-3 py-2 text-sm"
            >
              <option value="all">All Suggestions</option>
              <option value="popular">Most Popular</option>
              <option value="recent">Recent</option>
            </select>
          </div>
        </div>

        <Button onClick={() => setShowSubmitForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Submit Idea
        </Button>
      </div>
      {/* Feedback List */}
      <div className="grid gap-6">
        {filteredItems?.map((item, index) => (
          <motion.div
            key={item?.id}
            className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold mb-1">{item?.title}</h3>
                    <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                      <div className="flex items-center space-x-1">
                        <User className="w-4 h-4" />
                        <span>{item?.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{new Date(item.timestamp)?.toLocaleDateString()}</span>
                      </div>
                      <span className="px-2 py-1 bg-muted/20 rounded text-xs">
                        {item?.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item?.status)}`}>
                    {getStatusText(item?.status)}
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed">
                  {item?.description}
                </p>
              </div>

              <div className="lg:w-48 space-y-3">
                <div className="flex lg:flex-col gap-3">
                  <div className="bg-muted/5 rounded-lg p-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <ThumbsUp className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Votes</span>
                      </div>
                      <span className="font-semibold text-green-500">{item?.votes}</span>
                    </div>
                  </div>
                  
                  <div className="bg-muted/5 rounded-lg p-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Comments</span>
                      </div>
                      <span className="font-semibold">{item?.comments}</span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => handleVote(item?.id)}
                  >
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    Vote
                  </Button>
                  <Button size="sm" variant="outline" className="flex-1">
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Discuss
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {filteredItems?.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-muted/20 rounded-full flex items-center justify-center mb-4">
            <Search className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No suggestions found</h3>
          <p className="text-muted-foreground">Try adjusting your search or filters.</p>
        </div>
      )}
      {/* Submit Form Modal */}
      {showSubmitForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            className="bg-card border border-border rounded-xl p-6 w-full max-w-md"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-xl font-semibold mb-4">Submit New Idea</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <Input placeholder="Brief description of your idea..." />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <select className="w-full bg-background border border-border rounded-md px-3 py-2">
                  <option>UI/UX</option>
                  <option>Developer Tools</option>
                  <option>Testing Infrastructure</option>
                  <option>Mobile</option>
                  <option>Education</option>
                  <option>Community</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea 
                  className="w-full bg-background border border-border rounded-md px-3 py-2 h-24 resize-none"
                  placeholder="Detailed explanation of your idea and its benefits..."
                ></textarea>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setShowSubmitForm(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button className="flex-1">
                Submit Idea
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      {/* Stats */}
      <div className="bg-muted/5 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 text-center">Community Impact</h3>
        <div className="grid md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-500 mb-1">
              {feedbackItems?.length}
            </div>
            <div className="text-sm text-muted-foreground">Total Suggestions</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-green-500 mb-1">
              {feedbackItems?.filter(item => item?.status === 'planned')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Planned Features</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-purple-500 mb-1">
              {feedbackItems?.reduce((sum, item) => sum + item?.votes, 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total Votes Cast</div>
          </div>
          
          <div>
            <div className="text-2xl font-bold text-orange-500 mb-1">
              {feedbackItems?.reduce((sum, item) => sum + item?.comments, 0)}
            </div>
            <div className="text-sm text-muted-foreground">Total Comments</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityFeedback;